import { formatDate } from '@frontend/mwm-ui'

import type { ColDef, GetQuickFilterTextParams, ICellRendererParams, ValueGetterParams } from 'ag-grid-enterprise'
import type { ReactElement } from 'react'
import type { Menu } from '~/components/ActionMenu'
import type { IChecklist } from '~/types/checklist'
import type { IUserPublic } from '~/types/user'

import ActionMenu from '~/components/ActionMenu'
import { ChecklistStatusBadge } from '~/components/Badges'
import CreatedByCellRenderer from '~/components/CellRenderers/CreatedByCellRenderer'
import TeamCellRenderer from '~/components/CellRenderers/TeamCellRenderer'
import TextCellRenderer from '~/components/CellRenderers/TextCellRenderer'
import { CHECKLIST_STATUS_MAPPING } from '~/const'
import { textComparator } from '~/utils/comparators'
import { strDateFilterComparator } from '~/utils/date'
import { actionsColDef, createdByColDef, dateIsoColDef, lastUpdateColDef, teamColDef, textColDef } from '~/utils/table'

export interface IContext {
  application: string
  handleEditTodo: (data: IChecklist) => void
  handleRedirect: (url: string) => void
}

const ActionsCellRenderer = ({ context, data }: ICellRendererParams<IChecklist, unknown, IContext>): ReactElement => {
  if (data) {
    const { application, handleEditTodo, handleRedirect } = context
    const menu: Menu = {
      actions: [
        {
          action: () => handleEditTodo(data),
          label: 'Edit to-do list',
        },
      ],
      navigations: [
        {
          action: () => handleRedirect(`/versions/${data.application_semantic_version}?app=${application}`),
          label: 'Display related version',
          type: 'internal',
        },
        {
          action: () => handleRedirect(`/checklists/${data.id}`),
          label: 'Display to-do list',
          type: 'internal',
        },
      ],
    }

    return <ActionMenu menu={menu} placement="top" />
  }

  return <div />
}

const StatusCellRenderer = ({ data }: ICellRendererParams<IChecklist, IChecklist['status']>): ReactElement | null => {
  return data ? <ChecklistStatusBadge size="large" value={data.status} /> : null
}

const columns: ColDef[] = [
  textColDef(TextCellRenderer, {
    field: 'title',
    headerName: 'Name',
  }),
  {
    cellRenderer: StatusCellRenderer,
    field: 'status',
    filter: 'agSetColumnFilter',
    headerName: 'Status',
    headerTooltip: 'Status',
    menuTabs: ['filterMenuTab'],
    minWidth: 130,
    resizable: false,
    valueGetter: ({ data }: ValueGetterParams<IChecklist>): string | undefined => {
      if (data) {
        const { status } = data
        return CHECKLIST_STATUS_MAPPING[status]
      }
      return undefined
    },
    width: 130,
  },
  textColDef(TextCellRenderer, {
    field: 'application_semantic_version',
    headerName: 'Related version',
    headerTooltip: 'Related version',
    resizable: false,
    width: 100,
  }),
  dateIsoColDef({
    field: 'due_date',
    filterParams: {
      buttons: ['clear'],
      comparator: strDateFilterComparator,
    },
    getQuickFilterText: ({ value }: GetQuickFilterTextParams<IChecklist, IChecklist['due_date']>): string =>
      value ? formatDate(value) : '',
    headerName: 'Release date',
    headerTooltip: 'Release date',
  }),
  teamColDef(TeamCellRenderer, {
    field: 'team',
    headerTooltip: 'Team',
    valueGetter: ({ data }: ValueGetterParams<IChecklist>): string[] => {
      if (data) {
        const { assignation_mapping } = data
        const members = [
          ...new Set(Object.values(assignation_mapping).reduce((prev, users) => [...prev, ...users], [])),
        ]

        return members
      }
      return []
    },
  }),
  createdByColDef(CreatedByCellRenderer, {
    field: 'created_by',
    filterParams: {
      buttons: ['clear'],
      debounceMs: 1000,
      textCustomComparator: textComparator,
      trimInput: true,
      valueGetter: ({ data }: ValueGetterParams<IChecklist & { created_by?: IUserPublic }>): string =>
        data?.created_by ? data.created_by.name : '',
    },

    getQuickFilterText: ({ data }: GetQuickFilterTextParams<IChecklist & { created_by?: IUserPublic }>): string =>
      data?.created_by ? data.created_by.name : '',
    headerTooltip: 'Created by',
  }),
  lastUpdateColDef({
    field: 'updated_by',
    filter: 'agTextColumnFilter',
    filterParams: {
      buttons: ['clear'],
      debounceMs: 1000,
      textCustomComparator: textComparator,
      trimInput: true,
      valueGetter: ({ data }: ValueGetterParams<IChecklist & { updated_by?: IUserPublic }>): string =>
        data?.updated_by ? data.updated_by.name : '',
    },
    headerTooltip: 'Updated by',
  }),
  actionsColDef(ActionsCellRenderer, {
    field: 'actions',
  }),
]

export default columns
